import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SystemSettingsService } from '@services/system-settings.service';
import { DefaultTestsSettingDto } from '@models/default-tests-setting-dto.interface';
import { FluidConstantsSettingDto } from '@models/fluid-constants-setting-dto.interface';
import { TestTypeLookupDto } from '@models/test-type-lookup.interface';
import { JobsService } from '@services/jobs.service';
import { ProjectsService } from '@services/projects.service';
import { SupplierLookupDto } from '@models/project-lookups-dto.interface';
import { SystemSettingDto } from '@models/system-setting-dto.interface';
import { CreateUpdateSystemSettingDto } from '@models/create-update-system-setting-dto.interface';

interface TestTypeOption extends TestTypeLookupDto {
  selected: boolean;
}

@Component({
  selector: 'app-system-defaults-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatListModule,
    MatCheckboxModule,
  ],
  templateUrl: './system-defaults-modal.component.html',
  styleUrl: './system-defaults-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemDefaultsModalComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<SystemDefaultsModalComponent>
  );
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly jobsService = inject(JobsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly fb = inject(FormBuilder);

  protected isLoading = signal(true);
  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected successMessage = signal('');
  protected activeTabIndex = signal(0);

  // Tab-specific signals
  protected wbmTests = signal<TestTypeOption[]>([]);
  protected nafTests = signal<TestTypeOption[]>([]);
  protected suppliers = signal<SupplierLookupDto[]>([]);
  private defaultSupplierSetting = signal<SystemSettingDto | null>(null);

  // Form group for global fluid constants
  protected fluidConstantsForm: FormGroup = this.fb.group({
    bariteSG: [null, [Validators.required, Validators.min(0)]],
    baseFluidSG: [null, [Validators.required, Validators.min(0)]],
    lowGravitySolidsSG: [null, [Validators.required, Validators.min(0)]],
  });

  protected defaultSupplierForm: FormGroup = this.fb.group({
    defaultSupplierId: [null as number | null, [Validators.required]],
  });

  ngOnInit(): void {
    this.loadSystemDefaults();
  }

  private loadSystemDefaults(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.jobsService.getTestTypesForMudCheck().subscribe({
      next: (allTests) => {
        // Load WBM defaults
        this.systemSettingsService.getWbmDefaultTests().subscribe({
          next: (wbmDefaults) => {
            this.wbmTests.set(
              allTests.map((test) => ({
                ...test,
                selected: wbmDefaults.defaultTestIds.includes(test.id),
              }))
            );
          },
          error: () => {
            this.wbmTests.set(allTests.map((test) => ({ ...test, selected: false })));
          },
        });

        // Load NAF defaults
        this.systemSettingsService.getNafDefaultTests().subscribe({
          next: (nafDefaults) => {
            this.nafTests.set(
              allTests.map((test) => ({
                ...test,
                selected: nafDefaults.defaultTestIds.includes(test.id),
              }))
            );
          },
          error: () => {
            this.nafTests.set(allTests.map((test) => ({ ...test, selected: false })));
          },
        });
      },
      error: (err) => {
        this.errorMessage.set('Failed to load test types');
        console.error('Error loading test types:', err);
      },
    });

    // Load fluid constants (WBM as the default global constants)
    this.systemSettingsService.getFluidConstants('WBM').subscribe({
      next: (constants) => {
        this.fluidConstantsForm.patchValue({
          bariteSG: constants.bariteSG,
          baseFluidSG: constants.baseFluidSG,
          lowGravitySolidsSG: constants.lowGravitySolidsSG,
        });
      },
      error: () => {
        // Use default values if not found
        this.fluidConstantsForm.patchValue({
          bariteSG: 4.2,
          baseFluidSG: 1.0,
          lowGravitySolidsSG: 2.6,
        });
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });

    this.projectsService.getLookups().subscribe({
      next: (lookups) => {
        this.suppliers.set(lookups.suppliers);
      },
      error: (err) => {
        this.errorMessage.set('Failed to load suppliers');
        console.error('Error loading suppliers:', err);
      },
    });

    this.systemSettingsService.getSettingByKey('DEFAULT_SUPPLIER').subscribe({
      next: (setting) => {
        this.defaultSupplierSetting.set(setting);
        try {
          const supplierId = JSON.parse(setting.settingValue);
          if (typeof supplierId === 'number') {
            this.defaultSupplierForm.patchValue({ defaultSupplierId: supplierId });
          }
        } catch (err) {
          console.error('Error parsing default supplier setting:', err);
        }
      },
      error: (err) => {
        if (err.status !== 404) {
          this.errorMessage.set('Failed to load default supplier');
          console.error('Error loading default supplier:', err);
        }
      },
    });
  }

  private populateFluidConstantsForms(constants: FluidConstantsSettingDto[]): void {
    const wbmConst = constants.find((c) => c.fluidType === 'WBM');
    if (wbmConst) {
      this.fluidConstantsForm.patchValue({
        bariteSG: wbmConst.bariteSG,
        baseFluidSG: wbmConst.baseFluidSG,
        lowGravitySolidsSG: wbmConst.lowGravitySolidsSG,
      });
    }
  }

  protected toggleTestSelection(test: TestTypeOption): void {
    test.selected = !test.selected;
  }

  protected saveActiveTab(): void {
    switch (this.activeTabIndex()) {
      case 0: this.saveWbmDefaults(); break;
      case 1: this.saveNafDefaults(); break;
      case 2: this.saveFluidConstants(); break;
      case 3: this.saveDefaultSupplier(); break;
    }
  }

  protected saveWbmDefaults(): void {
    const selectedIds = this.wbmTests()
      .filter((t) => t.selected)
      .map((t) => t.id);

    this.isSaving.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');

    this.systemSettingsService.updateWbmDefaultTests(selectedIds).subscribe({
      next: () => {
        this.successMessage.set('WBM default tests saved successfully');
        this.isSaving.set(false);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        this.errorMessage.set('Failed to save WBM default tests');
        this.isSaving.set(false);
        console.error('Error saving WBM defaults:', err);
      },
    });
  }

  protected saveNafDefaults(): void {
    const selectedIds = this.nafTests()
      .filter((t) => t.selected)
      .map((t) => t.id);

    this.isSaving.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');

    this.systemSettingsService.updateNafDefaultTests(selectedIds).subscribe({
      next: () => {
        this.successMessage.set('NAF default tests saved successfully');
        this.isSaving.set(false);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        this.errorMessage.set('Failed to save NAF default tests');
        this.isSaving.set(false);
        console.error('Error saving NAF defaults:', err);
      },
    });
  }

  protected saveFluidConstants(): void {
    if (this.fluidConstantsForm.invalid) {
      this.errorMessage.set('Please fill in all required fields with valid values');
      return;
    }

    const constantsData = {
      bariteSG: this.fluidConstantsForm.get('bariteSG')?.value,
      baseFluidSG: this.fluidConstantsForm.get('baseFluidSG')?.value,
      lowGravitySolidsSG: this.fluidConstantsForm.get('lowGravitySolidsSG')?.value,
    };

    // Apply the same constants to all fluid types
    const fluidTypes: Array<'WBM' | 'SOBM' | 'DOBM'> = ['WBM', 'SOBM', 'DOBM'];
    
    this.isSaving.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');

    let completed = 0;
    let hasError = false;

    fluidTypes.forEach(fluidType => {
      const constants: FluidConstantsSettingDto = {
        fluidType,
        ...constantsData,
      };

      this.systemSettingsService.updateFluidConstants(constants).subscribe({
        next: () => {
          completed++;
          if (completed === fluidTypes.length) {
            this.isSaving.set(false);
            if (!hasError) {
              this.successMessage.set('Fluid constants saved successfully for all fluid types');
              setTimeout(() => this.successMessage.set(''), 3000);
            }
          }
        },
        error: (err) => {
          hasError = true;
          completed++;
          console.error(`Error saving fluid constants for ${fluidType}:`, err);
          if (completed === fluidTypes.length) {
            this.isSaving.set(false);
            this.errorMessage.set('Failed to save some fluid constants');
          }
        },
      });
    });
  }

  protected saveDefaultSupplier(): void {
    if (this.defaultSupplierForm.invalid) {
      this.errorMessage.set('Please select a default supplier');
      return;
    }

    const supplierId = this.defaultSupplierForm.get('defaultSupplierId')?.value as number;
    const setting: CreateUpdateSystemSettingDto = {
      settingKey: 'DEFAULT_SUPPLIER',
      settingValue: JSON.stringify(supplierId),
      category: 'SuppliersDefaults',
      description: 'Default supplier',
      isActive: true,
    };

    this.isSaving.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');

    const save$ = this.defaultSupplierSetting()
      ? this.systemSettingsService.updateSetting(this.defaultSupplierSetting()!.id, setting)
      : this.systemSettingsService.createSetting(setting);

    save$.subscribe({
      next: (savedSetting) => {
        this.defaultSupplierSetting.set(savedSetting);
        this.successMessage.set('Default supplier saved successfully');
        this.isSaving.set(false);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        this.errorMessage.set('Failed to save default supplier');
        this.isSaving.set(false);
        console.error('Error saving default supplier:', err);
      },
    });
  }
}
